import { isNativeApp, shareNativeImage } from 'common/webview';

/**
 * 사진 한 장 공유 — 사진 자체를 OS 공유 시트에 넘깁니다.
 *
 * 웹에서는 파일을 만들 수 없습니다. 실기기(폴드)에서 재 보니
 *   · navigator.share 가 없습니다(undefined)
 *   · CDN 이미지는 CORS 로 fetch 가 막혀 바이트를 읽을 수 없습니다
 * 그래서 앱에 맡깁니다. 앱이 내려받아 파일로 공유합니다.
 *
 * 앱이 이 기능을 모르면(구버전) 업데이트가 필요하다고 알립니다. 예전에는 주소를
 * 대신 보냈는데, 받는 쪽에 링크가 보이면 안 됩니다 — 사진을 보낸 것이 아니니까요.
 * 아무것도 안 보내고 이유를 말하는 편이 낫습니다.
 * 브라우저에서는 주소 공유나 복사로 물러섭니다.
 *
 * @returns 무엇으로 처리했는지 — 부르는 쪽이 안내 문구를 고릅니다
 */
export type PhotoShareResult =
    /** 앱이 사진 파일을 공유 시트에 넘겼습니다 */
    | 'native'
    /** 앱이 이 기능을 모릅니다(구버전) — 업데이트가 필요합니다 */
    | 'needsAppUpdate'
    /** 브라우저의 공유 시트로 넘겼습니다 */
    | 'web'
    /** 공유 시트가 없어 주소를 복사했습니다 */
    | 'copied'
    | 'failed';

export const sharePhoto = async (
    url: string,
    title?: string,
): Promise<PhotoShareResult> => {
    if (!url) return 'failed';

    if (isNativeApp()) {
        /* 사진 자체를 보냅니다. 앱이 이 기능을 모르면 곧바로 돌아오므로, 그때는
           주소를 보내지 않고 업데이트가 필요하다고 부르는 쪽에 알립니다. */
        const r = await shareNativeImage(url, title);
        return r.timedOut ? 'needsAppUpdate' : 'native';
    }

    const nav = navigator as any;
    if (typeof nav.share === 'function') {
        try {
            await nav.share({ title, url });
            return 'web';
        } catch {
            /* 사용자가 공유 시트를 닫은 것도 여기로 옵니다 — 실패로 알리지 않습니다 */
            return 'web';
        }
    }

    try {
        await navigator.clipboard.writeText(url);
        return 'copied';
    } catch {
        return 'failed';
    }
};
